import { c as _c } from "react/compiler-runtime";
import type { FieldError } from '@onefootprint/hooks';
import { IcoUserCircle24 } from '@onefootprint/icons';
import type { HostedBusinessOwner } from '@onefootprint/request-types';
import { Divider, Form, InlineAlert, LinkButton, useToast } from '@onefootprint/ui';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import getRandomUuid from '@/idv2/utils/get-random-uuid/get-random-uuid';
import type { ManageBosFormData, NewBusinessOwner } from '../manage-business-owners.types';
import { hasDuplicatedEmail, hasDuplicatedPhoneNumber, sumTotalOwnershipStake } from '../utils/manage-bos-utils';
import { AddBoButton } from './add-bo-button';
const vaultKeyToFormField: Record<string, string> = {
  'id.first_name': 'firstName',
  'id.last_name': 'lastName',
  'id.email': 'email',
  'id.phone_number': 'phoneNumber'
};
export type BosFormProps = {
  existingBos: HostedBusinessOwner[];
  onSubmit: (formData: ManageBosFormData, onFieldError: (errors: FieldError[]) => void) => void;
  // If there is bootstrap data or existing BOs, we can use them to pre-populate the form.
  defaultFormValues: NewBusinessOwner[];
  allowDuplicateContactInfo: boolean;
  formId?: string;
};

/** Renders a form for editing the mutable beneficial owners of a business or adding new beneficial owners. */
export const BosForm = t0 => {
  const $ = _c(53);
  const {
    existingBos,
    onSubmit,
    defaultFormValues,
    allowDuplicateContactInfo,
    formId: t1
  } = t0;
  const formId = t1 === undefined ? "bos-form" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "collectBusinessData.beneficialOwners.form"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t2);
  const {
    toast
  } = useToast();
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== defaultFormValues) {
    t4 = {
      defaultValues: {
        bos: defaultFormValues,
        bosToDelete: t3
      }
    };
    $[2] = defaultFormValues;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const methods = useForm(t4);
  const {
    control,
    handleSubmit,
    getValues,
    setValue
  } = methods;
  let t5;
  if ($[4] !== control) {
    t5 = {
      name: "bos",
      control
    };
    $[4] = control;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    append,
    fields,
    remove
  } = useFieldArray(t5);
  let t6;
  if ($[6] !== getValues || $[7] !== setValue) {
    t6 = uuid => {
      const currentBosToDelete = getValues("bosToDelete") || [];
      setValue("bosToDelete", [...currentBosToDelete, uuid]);
    };
    $[6] = getValues;
    $[7] = setValue;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const addBoToDelete = t6;
  let t7;
  if ($[9] !== append) {
    t7 = () => {
      append(getEmptyBo());
    };
    $[9] = append;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleAdd = t7;
  let t8;
  if ($[11] !== fields || $[12] !== methods) {
    t8 = errors => {
      for (const error of errors) {
        const formField = vaultKeyToFormField[error.key];
        if (formField) {
          fields.forEach((_, idx) => {
            methods.setError(`bos.${idx}.${formField}` as keyof ManageBosFormData, {
              message: error.message
            });
          });
        }
      }
    };
    $[11] = fields;
    $[12] = methods;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const onFieldError = t8;
  let t9;
  if ($[14] !== allowDuplicateContactInfo || $[15] !== existingBos || $[16] !== onFieldError || $[17] !== onSubmit || $[18] !== t || $[19] !== toast) {
    t9 = formData => {
      const totalOwnershipStake = sumTotalOwnershipStake(existingBos, formData);
      if (totalOwnershipStake > 100) {
        toast({
          title: t(_temp),
          description: t(_temp2),
          variant: "error"
        });
        return;
      }
      if (!allowDuplicateContactInfo && hasDuplicatedEmail(existingBos, formData)) {
        toast({
          title: t(_temp3),
          description: t(_temp4),
          variant: "error"
        });
        return;
      }
      if (!allowDuplicateContactInfo && hasDuplicatedPhoneNumber(existingBos, formData)) {
        toast({
          title: t(_temp5),
          description: t(_temp6),
          variant: "error"
        });
        return;
      }
      onSubmit(formData, onFieldError);
    };
    $[14] = allowDuplicateContactInfo;
    $[15] = existingBos;
    $[16] = onFieldError;
    $[17] = onSubmit;
    $[18] = t;
    $[19] = toast;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const onSubmitFormData = t9;
  let t10;
  if ($[21] !== handleSubmit || $[22] !== onSubmitFormData) {
    t10 = handleSubmit(onSubmitFormData);
    $[21] = handleSubmit;
    $[22] = onSubmitFormData;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== addBoToDelete || $[25] !== existingBos || $[26] !== fields || $[27] !== remove || $[28] !== t) {
    let t12;
    if ($[30] !== addBoToDelete || $[31] !== existingBos || $[32] !== remove || $[33] !== t) {
      t12 = (field, idx_0) => {
        const backendBo = existingBos.find(bo => bo.uuid === field.uuid);
        const handleRemoveClick = () => {
          remove(idx_0);
          if (backendBo) {
            addBoToDelete(backendBo.uuid);
          }
        };
        const isPrimaryBo = !!backendBo?.isAuthedUser;
        const isMutable = !backendBo || backendBo.isMutable;
        const showPhoneAndEmail = isMutable && !isPrimaryBo;
        return <div key={field.id} className="flex flex-col border border-tertiary rounded-sm mt-4 p-4"><div className="flex items-center gap-2 mb-4"><IcoUserCircle24 color="primary" /><h3 className="text-label-2 text-primary">{isPrimaryBo ? t(_temp7) : t(_temp8)}</h3>{!isPrimaryBo && <LinkButton className="ml-auto" onClick={handleRemoveClick}>{t(_temp9)}</LinkButton>}</div><div className="flex flex-col gap-5">{isPrimaryBo && <InlineAlert variant="info"><span className="text-body-2 text-info">{t(_temp10)}</span></InlineAlert>}<Form.Input name={`bos.${idx_0}.firstName`} label={t(_temp11)} data-dd-privacy="mask" data-dd-action-name="First name input" placeholder={t(_temp12)} disabled={!isMutable} rules={{
              required: t(_temp13)
            }} /><Form.Input name={`bos.${idx_0}.lastName`} label={t(_temp14)} data-dd-privacy="mask" data-dd-action-name="Last name input" placeholder={t(_temp15)} disabled={!isMutable} rules={{
              required: t(_temp16)
            }} />{showPhoneAndEmail && <Form.Email name={`bos.${idx_0}.email`} label={t(_temp17)} data-dd-privacy="mask" data-dd-action-name="Email input" placeholder={t(_temp18)} rules={{
              required: t(_temp19)
            }} />}{showPhoneAndEmail && <Form.Tel name={`bos.${idx_0}.phoneNumber`} label={t(_temp20)} data-dd-privacy="mask" data-dd-action-name="Phone input" rules={{
              required: t(_temp21)
            }} />}<Form.Number name={`bos.${idx_0}.ownershipStake`} label={t(_temp22)} data-dd-privacy="mask" data-dd-action-name="Ownership stake input" placeholder="100" decimalPlaces={0} allowNegative={false} thousandsSeparator={false} rules={{
              required: t(_temp23),
              min: {
                value: 0,
                message: t(_temp24)
              },
              max: {
                value: 100,
                message: t(_temp25)
              }
            }} /></div></div>;
      };
      $[30] = addBoToDelete;
      $[31] = existingBos;
      $[32] = remove;
      $[33] = t;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t11 = fields.map(t12);
    $[24] = addBoToDelete;
    $[25] = existingBos;
    $[26] = fields;
    $[27] = remove;
    $[28] = t;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[35] !== fields.length || $[36] !== t) {
    t12 = fields.length > 0 ? <span className="text-body-2 text-primary mt-4">{t(_temp26)}</span> : null;
    $[35] = fields.length;
    $[36] = t;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Divider className="my-5" />;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== existingBos || $[40] !== handleAdd) {
    t14 = <div className="flex"><AddBoButton existingBos={existingBos} onAdd={handleAdd} /></div>;
    $[39] = existingBos;
    $[40] = handleAdd;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t11 || $[43] !== t12 || $[44] !== t14) {
    t15 = <div className="flex flex-col w-full">{t11}{t12}{t13}{t14}</div>;
    $[42] = t11;
    $[43] = t12;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== formId || $[47] !== t10 || $[48] !== t15) {
    t16 = <form id={formId} onSubmit={t10}>{t15}</form>;
    $[46] = formId;
    $[47] = t10;
    $[48] = t15;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== methods || $[51] !== t16) {
    t17 = <FormProvider {...methods}>{t16}</FormProvider>;
    $[50] = methods;
    $[51] = t16;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  return t17;
};
const getEmptyBo = () => {
  return {
    uuid: getRandomUuid(),
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    ownershipStake: undefined
  };
};
function _temp($) {
  return $.errors["ownership-stake-total"].title;
}
function _temp2($_0) {
  return $_0.errors["ownership-stake-total"].description;
}
function _temp3($_1) {
  return $_1.errors["duplicate-email"].title;
}
function _temp4($_2) {
  return $_2.errors["duplicate-email"].description;
}
function _temp5($_3) {
  return $_3.errors["duplicate-phone-number"].title;
}
function _temp6($_4) {
  return $_4.errors["duplicate-phone-number"].description;
}
function _temp7($_5) {
  return $_5["fields-header"]["beneficial-owner-you"];
}
function _temp8($_6) {
  return $_6["fields-header"]["beneficial-owner"];
}
function _temp9($_7) {
  return $_7["fields-header"].remove;
}
function _temp10($_8) {
  return $_8.fields["primary-bo-name-hint-new"];
}
function _temp11($_9) {
  return $_9.fields["first-name"].label;
}
function _temp12($_10) {
  return $_10.fields["first-name"].placeholder;
}
function _temp13($_11) {
  return $_11.fields["first-name"].error;
}
function _temp14($_12) {
  return $_12.fields["last-name"].label;
}
function _temp15($_13) {
  return $_13.fields["last-name"].placeholder;
}
function _temp16($_14) {
  return $_14.fields["last-name"].error;
}
function _temp17($_15) {
  return $_15.fields.email.label;
}
function _temp18($_16) {
  return $_16.fields.email.placeholder;
}
function _temp19($_17) {
  return $_17.fields.email.errors.required;
}
function _temp20($_18) {
  return $_18.fields.phone.label;
}
function _temp21($_19) {
  return $_19.fields.phone.errors.required;
}
function _temp22($_20) {
  return $_20.fields["ownership-stake"].label;
}
function _temp23($_21) {
  return $_21.fields["ownership-stake"].errors.required;
}
function _temp24($_22) {
  return $_22.fields["ownership-stake"].errors.min;
}
function _temp25($_23) {
  return $_23.fields["ownership-stake"].errors.max;
}
function _temp26($_24) {
  return $_24["multi-kyc"];
}